import * as React from "react";
import { Link } from "react-router-dom";
import { MoreHorizontal, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { OFFLINE_MSG } from "@/lib/offline";

/**
 * FAB Batch A — shared field standard
 * - Position: above BottomNav + safe-area
 * - Size: 56×56 trigger
 * - Offline: network-required actions toast OFFLINE_MSG (no outbox in 1A)
 * - Create: RouteFab in the shell (single action or speed-dial). This Fab remains the 56px circle.
 */
/** Above floating BottomNav (pill + 0.75rem float + safe-area). */
export const fabPositionClass =
  "fixed right-4 z-[45] flex flex-col items-end bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] print:hidden lg:bottom-6";

const fabShellClass = fabPositionClass;

export const fabTriggerClass =
  "flex h-14 w-14 min-h-[56px] min-w-[56px] items-center justify-center rounded-full bg-amber-500 text-slate-950 shadow-lg transition-transform duration-200 ease-out hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 active:scale-95 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-slate-950";

type FabIcon = React.ReactNode;

function runFabAction(requiresOnline: boolean, online: boolean, action: () => void) {
  if (requiresOnline && !online) {
    toast.error(OFFLINE_MSG);
    return;
  }
  action();
}

export type FabProps = {
  label: string;
  onClick: () => void;
  icon?: FabIcon;
  disabled?: boolean;
  /** Default true — create/save actions need network (1A: no write queue). */
  requiresOnline?: boolean;
  className?: string;
  /** Defaults to `fab` */
  testId?: string;
};

/**
 * Single-action FAB for mobile primary create (desktop uses Header accent Button).
 */
export function Fab({
  label,
  onClick,
  icon,
  disabled,
  requiresOnline = true,
  className,
  testId = "fab"
}: FabProps) {
  const online = useOnlineStatus();
  const blocked = Boolean(disabled || (requiresOnline && !online));

  return (
    <div className={cn(fabShellClass, className)} data-testid="fab-anchor">
      <button
        type="button"
        data-testid={testId}
        aria-label={label}
        aria-disabled={blocked || undefined}
        disabled={blocked}
        title={blocked && requiresOnline && !online ? OFFLINE_MSG : undefined}
        onClick={() => runFabAction(requiresOnline, online, onClick)}
        className={fabTriggerClass}
      >
        {icon ?? <Plus className="h-6 w-6" aria-hidden />}
      </button>
    </div>
  );
}

export type OverflowMenuItem = {
  id: string;
  label: string;
  onClick?: () => void;
  /** SPA path — prefer over href for in-app routes */
  to?: string;
  href?: string;
  icon?: FabIcon;
  disabled?: boolean;
};

/**
 * Compact header overflow (⋯) for secondary actions — M8 header declutter.
 */
export function OverflowMenu({
  label = "További műveletek",
  items,
  className
}: {
  label?: string;
  items: OverflowMenuItem[];
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const el = rootRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("touchstart", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("touchstart", onPointer);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative print:hidden", className)}>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        data-testid="header-overflow-menu"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal className="h-5 w-5" aria-hidden />
      </Button>
      {open ? (
        <ul
          role="menu"
          aria-label={label}
          className="absolute right-0 z-50 mt-2 min-w-[12rem] list-none rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-800 dark:bg-slate-950"
        >
          {items.map((item) => {
            const content = (
              <>
                {item.icon ? <span className="shrink-0">{item.icon}</span> : null}
                <span>{item.label}</span>
              </>
            );
            const itemClass = "w-full justify-start";
            if (item.to) {
              return (
                <li key={item.id} role="none">
                  <Button asChild variant="ghost" className={itemClass}>
                    <Link role="menuitem" to={item.to} onClick={() => setOpen(false)}>
                      {content}
                    </Link>
                  </Button>
                </li>
              );
            }
            if (item.href) {
              return (
                <li key={item.id} role="none">
                  <Button asChild variant="ghost" className={itemClass}>
                    <a role="menuitem" href={item.href} onClick={() => setOpen(false)}>
                      {content}
                    </a>
                  </Button>
                </li>
              );
            }
            return (
              <li key={item.id} role="none">
                <Button
                  type="button"
                  role="menuitem"
                  variant="ghost"
                  disabled={item.disabled}
                  className={itemClass}
                  onClick={() => {
                    setOpen(false);
                    item.onClick?.();
                  }}
                >
                  {content}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** Extra bottom padding so list content clears the FAB (phones only; header buttons from 768px). */
export const fabContentPadClass = "max-md:pb-28";
